<template>
  <div class="block-hero-products">
    <div class="block-hero-products__grid">
      <div class="block-hero-products__image-container">
        <AtomImage
          v-if="imageSwitchData && !is360"
          :key="imageSwitchData"
          behaviour="contain"
          :data="imageSwitchData"
          :is-strapi="true"
          class="block-hero-products__image"
        />

        <AtomImage360
          v-if="imageSwitchData && is360 && image360IsValid"
          :url="product.image360"
        />

        <div class="block-hero-products__image-flags-container">
          <div class="block-hero-products__image-flags-product-controls">
            <ClientOnly>
              <ShopProductControls
                v-if="user && bookMarksInitialized"
                :has-bookmark-icon="true"
                :has-calendar-icon="false"
                :has-loved-icon="false"
                :has-helped-icon="false"
                :has-cart-icon="false"
              />
            </ClientOnly>
          </div>

          <AtomImageSwitch
            :has-valid-360="image360IsValid"
            @active-image="imageSwitchDataHandler"
            @set-image-360-mode="switch360Mode"
          />
        </div>
      </div>
      <div class="block-hero-products__content">
        <div class="block-hero-products__content-flags">
          <ShopProductCardDiscount
            v-if="item.discount?.price"
            :is-hero="true"
          />
          <AtomClickAndCollectNotice
            appereance="filled"
            :force-info="true"
          />
        </div>
        <div class="block-hero-products__content-name">
          {{ product?.brand?.name }}
        </div>

        <AtomHeading
          class="block-hero-products__content-label"
          :text="product?.label"
          tag="h1"
          appereance="h1"
        />
        <AtomLead
          class="block-hero-products__content-lead"
          :text="parseHTMLString(product?.short_text)"
        />
        <ShopProductCardPrice
          class="block-hero-products__content-price"
          :is-hero="true"
        />

        <div v-if="product?.deliveryTime?.key !== 'delivery_time_ah' && product?.deliveryTime?.key !== 'delivery_time_nl'" class="block-hero-products__content-add-to-cart">
          <AtomButton :text="useTranslation('general', 'add_to_cart')" @click="addProduct()" />
        </div>
        <div v-else class="block-hero-products__content-not-available">
          <span v-if="product?.deliveryTime?.key === 'delivery_time_ah'">{{ useTranslation('general', 'delivery_time_ah') }}</span>
          <span v-if="product?.deliveryTime?.key === 'delivery_time_nl'">{{ useTranslation('general', 'delivery_time_nl') }}</span>
        </div>
        <div v-if="attributes && attributes?.length > 0" class="block-hero-products__content-attributes">
          <div
            v-for="(attribute, index) in attributes"
            :key="index"
            class="block-hero-products__content-attribute"
          >
            <AtomImage v-if="attribute?.icon" :data="attribute?.icon" :is-strapi="true" />
            {{ attribute?.display_name }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
defineProps({
    data: {
        type: Object,
        default: () => {},
    },
});

const user = useStrapiUser();

/*
  Get cart store
*/
const cartStore = useCartStore();

/*
  Injected Data
*/
const item = inject('item');
const product = inject('product');

/*
  Add product to cart
*/
const addProduct = () => {
    cartStore.addProduct(item);
};

/*
  Provide for interactive order items
*/
provide('is-editable', true);

/*
  Get attributes
*/
const attributes = computed(() => product?.attributes);

/*
  360 Mode
*/
const is360 = ref(false);
const switch360Mode = (mode) => {
    is360.value = mode;
};

/*
  Get emmited data from atom image switch
*/
const imageSwitchData = ref(null);
const imageSwitchDataHandler = (data) => {
    imageSwitchData.value = data;
};

const bookmarkStore = useBookmarkStore();
const bookMarksInitialized = computed(() => bookmarkStore.getInitialized);

/*
  Check if 360 image is valid and returns 200
*/
const image360IsValid = ref(false);
onMounted(() => {
    image360IsValid.value = urlExists(product?.image360);
});
</script>

<style lang="scss">
.block-hero-products {
    @include fluid('margin-bottom', 20px, 40px);

    overflow: hidden;
    background-color: $C_GREEN_BRIGHT;
    border-bottom-right-radius: 280px;
    isolation: isolate;

    @include mobile {
        height: 100%;
        min-height: unset;
        border-bottom-right-radius: 50px;
    }
}

.block-hero-products__grid {
    @include grid-layout();

    position: relative;
    width: 100%;
    max-width: var(--grid-width);
    height: 100%;
    padding: 0 var(--page-padding);
    margin: 0 auto;
}

.block-hero-products__brand-logo {
    @include grid-columns(1);
    @include fluid('width', 103px, 227px);
    @include fluid('bottom', 20px, 80px);

    position: absolute;
    display: flex;
    overflow: hidden;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
    aspect-ratio: 1.31;
    background-color: $C_WHITE;

    img {
        position: absolute;
        top: 0;
        left: 20%;
        width: 60%;
        height: 100%;
        object-fit: contain;
        object-position: center center;
    }
}

.block-hero-products__image-container {
    @include grid-columns(1, 8);

    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;

    @include mobile {
        height: 520px;
    }

    .block-hero-products__image {
        position: absolute;
        display: flex;
        width: 100%;
        height: 100%;

        img, .atom-image-360 {
            padding-top: 220px;
            padding-bottom: 140px;
        }

        @include mobile {
            img, .atom-image-360 {
                padding-top: 180px;
                padding-bottom: 100px;
            }

            margin-top: 136px;
            margin-bottom: 40px;
        }
    }

    .block-hero-products__image-flags-container {
        @include fluid('margin-bottom', 20px, 60px);
        @include z-index('bullets');

        display: flex;
        width: 100%;
        align-items: flex-end;
        justify-content: space-between;
        justify-self: flex-end;

        @include mobile {

            flex-direction: column-reverse;
            align-items: flex-start;
        }
    }

    .block-hero-products__image-flag {
        padding: 15px 20px;
        border-radius: 6px;
        background-color: $C_WHITE;
    }

    @include tablet {
        @include grid-columns(1, 6);
    }
}

.block-hero-products__content {
    @include grid-columns(9, 10);

    position: relative;
    display: flex;
    overflow: hidden;
    height: 100%;
    flex-direction: column;
    padding-top: 209px;
    padding-right: 185px;
    padding-left: 87px;
    margin-right: calc(0px - var(--grid-offset));
    background-color: $C_GREEN__HERO;
    border-top-left-radius: 120px;

    .block-hero-products__content-flags {
        @include fluid('margin-bottom', 21px, 30px);

        display: flex;
        flex-direction: row;

        .block-hero-products__content-discount {
            @include typo-font('regular');
            @include typo-size('nav');

            display: flex;
            align-items: center;
            justify-content: center;
            padding: 15px 20px;
            border-radius: 6px;
            margin-right: 10px;
            background-color: $C_RED;
            color: $C_WHITE;
        }
    }

    .block-hero-products__content-name {
        @include typo-font('regular');
        @include typo-size('pretitel');

        color: $C_GREEN_DARK;
    }

    .block-hero-products__content-label {
        @include fluid('margin-bottom', 10px, 15px);
        @include z-index('main');
    }

    .block-hero-products__content-lead {
        @include fluid('margin-bottom', 40px, 71px);
        @include z-index('main');
    }

    .block-hero-products__content-price {
        @include fluid('margin-bottom', 13px, 30px);
        @include z-index('main');

        display: flex;
        flex-direction: column-reverse;
    }

    .block-hero-products__content-add-to-cart {
        @include fluid('margin-bottom', 40px, 80px);

        display: flex;
    }

    .block-hero-products__content-not-available {
        @include fluid('margin-bottom', 40px, 80px);

        display: flex;

        span {
            @include typo-size('button');
            @include typo-font('regular');
            @include fluid('min-width', 152px, 172px);
            @include fluid('height', 49px, 59px);

            z-index: 3;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 20px 30px;
            border: 2px solid $C_SECONDARY;
            border-radius: 6px;
            background: $C_SECONDARY;
            color: $C-WHITE;
            outline: 0;
            text-align: center;
            transition: background-color 0.25s ease-in-out, opacity 0.25s ease-in-out, border 0.25s ease-in-out, color 0.25s ease-in-out;
            white-space: nowrap;
        }
    }

    .block-hero-products__content-attributes {
        z-index: 1;
        bottom: 50px;
        display: flex;
        flex-wrap: wrap;
        margin-bottom: 60px;

        .block-hero-products__content-attribute {
            @include typo-font('regular');
            @include typo-size('nav');

            display: flex;
            height: 54px;
            align-items: center;
            padding: 0 20px 0 20px;
            border-radius: 6px;
            margin-right: 10px;
            background-color: $C_WHITE;
            color: $C_GREEN_DARK;

            @include mobile {
                margin-bottom: 10px;
            }
        }

        .atom-image {
            margin-right: 10px;
        }

        @include mobile {
            position: relative;
            bottom: 0;
            margin-bottom: 48px;
        }
    }

    @include tablet {
        @include grid-columns(7, 6);
    }

    @include mobile {
        @include grid-columns(2, 4);
    }

    &:before {
        position: absolute;
        right: -140px;
        bottom: -230px;
        width: 563px;
        height: 618px;
        background-color: $C_PRIMARY;
        content: '';
        filter: blur(165px);
        opacity: 0.75;
        transform: matrix(-0.87, -0.49, 0.5, -0.87, 0, 0);

        @include mobile {
            display: none;
        }
    }

    &:after {
        position: absolute;
        right: 244px;
        bottom: 72px;
        width: 143px;
        height: 143px;
        background-color: $C_GREEN_DARK;
        content: '';
        filter: blur(114.5px);
        transform: matrix(-0.87, -0.49, 0.5, -0.87, 0, 0);

        @include mobile {
            display: none;
        }
    }

    @include tablet {
        padding-right: 20px;
        padding-left: 30px;
    }

    @include mobile {
        padding-top: 40px;
        margin-left: calc(0px - var(--grid-offset));
        border-top-left-radius: 50px;
    }
}
</style>
